import React, { useState, useEffect } from "react";
import { db, ref, onValue, get } from "./firebase";
import { auth } from "./firebase";
import { FiSearch, FiPlay, FiChevronDown, FiChevronUp, FiVideo } from "react-icons/fi";

const Videos = () => {
  const [videos, setVideos] = useState([]);
  const [enrolledCourse, setEnrolledCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [activeVideoId, setActiveVideoId] = useState(null);
  const [expandedChapters, setExpandedChapters] = useState({});

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          setError("User not authenticated. Please log in.");
          setLoading(false);
          return;
        }

        const email = currentUser.email;
        let foundUser = null;
        let userCourse = null;

        // First check if user is admin/assistant in users collection
        const usersRef = ref(db, "users");
        const usersSnapshot = await get(usersRef);

        if (usersSnapshot.exists()) {
          const usersData = usersSnapshot.val();
          foundUser = Object.values(usersData).find(
            (u) => u.email === email
          );
          if (foundUser) {
            userCourse = foundUser.enrolledCourse;
          }
        }

        // If not found in users collection, search in courses/students
        if (!foundUser) {
          const coursesRef = ref(db, "courses");
          const coursesSnapshot = await get(coursesRef);

          if (coursesSnapshot.exists()) {
            const courses = coursesSnapshot.val();

            // Search through each course's students
            for (const [courseId, courseData] of Object.entries(courses)) {
              if (courseData.students) {
                const student = Object.values(courseData.students).find(
                  (s) => s.email === email
                );
                if (student) {
                  foundUser = student;
                  userCourse = courseId;
                  break;
                }
              }
            }
          }
        }

        if (foundUser && userCourse) {
          setEnrolledCourse(userCourse);
        } else {
          setError("No enrolled course found. Please enroll in a course.");
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        setError("Failed to fetch user data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    if (!enrolledCourse) return;

    const videosRef = ref(db, `courses/${enrolledCourse}/videos`);
    const unsubscribeVideos = onValue(
      videosRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const videoData = snapshot.val();
          // Filter videos to only include published ones (published !== false)
          const videoList = Object.keys(videoData)
            .map((key) => ({
              id: key,
              ...videoData[key],
            }))
            .filter(video => video.published !== false) // Only show published videos
            .sort((a, b) => (a.order || 0) - (b.order || 0)); // Sort by order if available

          setVideos(videoList);
          setFilteredVideos(videoList);

          // Initialize expanded state for chapters
          const chapters = [...new Set(videoList.map(video => video.chapter || "Uncategorized"))];
          const initialExpandedState = {};
          chapters.forEach(chapter => {
            initialExpandedState[chapter] = true; // All chapters expanded by default
          });
          setExpandedChapters(initialExpandedState);
        } else {
          setVideos([]);
          setFilteredVideos([]);
          setError("No videos found for the enrolled course.");
        }
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching videos:", error);
        setError("Failed to fetch videos. Please try again later.");
        setLoading(false);
      }
    );

    return () => unsubscribeVideos();
  }, [enrolledCourse]);

  useEffect(() => {
    if (searchQuery) {
      const filtered = videos.filter((video) =>
        video.title.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredVideos(filtered);
    } else {
      setFilteredVideos(videos);
    }
  }, [searchQuery, videos]);

  const toggleVideo = (videoId) => {
    setActiveVideoId(activeVideoId === videoId ? null : videoId);
  };

  const toggleChapter = (chapter) => {
    setExpandedChapters(prev => ({
      ...prev,
      [chapter]: !prev[chapter]
    }));
  };

  // Function to convert video URL to embed URL
  const getEmbedUrl = (url) => {
    if (!url) return null;

    // Handle YouTube URLs
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      let videoId = null;
      if (url.includes('youtube.com/watch?v=')) {
        videoId = url.split('v=')[1]?.split('&')[0];
      } else if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1]?.split('?')[0];
      } else if (url.includes('youtube.com/embed/')) {
        videoId = url.split('embed/')[1]?.split('?')[0];
      }
      return videoId ? `https://www.youtube.com/embed/${videoId}?rel=0` : null;
    }

    // Handle Vimeo URLs
    if (url.includes('vimeo.com')) {
      let videoId = null;

      // Handle regular vimeo.com URLs
      if (url.includes('vimeo.com/')) {
        // Extract video ID from various Vimeo URL formats
        const match = url.match(/vimeo\.com\/(\d+)/);
        if (match) {
          videoId = match[1];
        }
      }

      // Handle player.vimeo.com URLs
      if (url.includes('player.vimeo.com')) {
        const match = url.match(/player\.vimeo\.com\/video\/(\d+)/);
        if (match) {
          videoId = match[1];
        }
      }

      // Handle vimeo.com/embed/ URLs
      if (url.includes('vimeo.com/embed/')) {
        const match = url.match(/vimeo\.com\/embed\/(\d+)/);
        if (match) {
          videoId = match[1];
        }
      }

      return videoId ? `https://player.vimeo.com/video/${videoId}?autoplay=0&title=0&byline=0&portrait=0` : null;
    }

    // Handle Google Drive URLs
    if (url.includes('drive.google.com')) {
      if (url.includes('/file/d/')) {
        const fileId = url.split('/file/d/')[1]?.split('/')[0];
        return fileId ? `https://drive.google.com/file/d/${fileId}/preview` : null;
      } else if (url.includes('id=')) {
        const fileId = url.split('id=')[1]?.split('&')[0];
        return fileId ? `https://drive.google.com/file/d/${fileId}/preview` : null;
      }
    }

    // Handle Zoom Recording URLs
    if (url.includes('zoom.us/rec')) {
      // Handle us02web.zoom.us format
      if (url.includes('us02web.zoom.us')) {
        const sharePart = url.split('/rec/share/')[1];
        if (sharePart) {
          const [recordingId, token] = sharePart.split('.');
          if (recordingId && token) {
            return `https://us02web.zoom.us/rec/play/${recordingId}?token=${token}&embedded=true&autoplay=0&mode=play`;
          }
        }
      }
      // Handle regular zoom.us format
      else if (url.includes('/share/')) {
        const sharePart = url.split('/share/')[1];
        if (sharePart) {
          const [recordingId, token] = sharePart.split('?');
          if (recordingId && token) {
            return `https://zoom.us/recording/play/${recordingId}?token=${token}&embedded=true&autoplay=0&mode=play`;
          }
        }
      } else if (url.includes('/play/')) {
        const baseUrl = url.split('?')[0];
        const params = new URLSearchParams(url.split('?')[1] || '');
        params.set('embedded', 'true');
        params.set('autoplay', '0');
        params.set('mode', 'play');
        return `${baseUrl}?${params.toString()}`;
      }
    }

    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="bg-red-500/10 p-6 rounded-xl w-full max-w-md text-center border border-red-500/20">
          <div className="text-red-400 font-medium text-lg mb-2">Error</div>
          <p className="text-gray-300">{error}</p>
        </div>
      </div>
    );
  }

  // Group videos by chapter
  const videosByChapter = filteredVideos.reduce((acc, video) => {
    const chapter = video.chapter || "Uncategorized";
    if (!acc[chapter]) {
      acc[chapter] = [];
    }
    acc[chapter].push(video);
    return acc;
  }, {});

  return (
    <div className="mx-auto px-4 py-6 w-full">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-teal-600 to-sky-600 rounded-2xl p-6 md:p-8 shadow-xl mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Course Videos</h1>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-teal-100 text-sm md:text-base">Total Videos:</span>
            <span className="text-white font-bold text-lg md:text-xl">{filteredVideos.length}</span>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-4 mb-6">
        <div className="relative">
          <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search videos..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-gray-700/50 text-white pl-12 pr-4 py-3 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent placeholder-gray-400"
          />
        </div>
      </div>

      {/* Video List by Chapter */}
      <div className="grid grid-cols-1 gap-4 md:gap-6">
        {Object.entries(videosByChapter).map(([chapter, chapterVideos]) => (
          <div key={chapter} className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 overflow-hidden">
            {/* Chapter Header */}
            <div
              className="p-4 bg-gray-700/50 cursor-pointer flex items-center justify-between"
              onClick={() => toggleChapter(chapter)}
            >
              <h2 className="text-xl font-semibold text-white">{chapter}</h2>
              <button className="text-gray-400 hover:text-white transition-colors">
                {expandedChapters[chapter] ? (
                  <FiChevronUp className="text-xl" />
                ) : (
                  <FiChevronDown className="text-xl" />
                )}
              </button>
            </div>

            {/* Chapter Videos */}
            {expandedChapters[chapter] && (
              <div className="p-4 space-y-4">
                {chapterVideos.map((video) => (
                  <div
                    key={video.id}
                    className="bg-gray-700/30 rounded-xl border border-gray-600/50 overflow-hidden hover:border-teal-500/30 transition-all duration-300"
                  >
                    <div className="p-6">
                      <div
                        className="flex items-start justify-between cursor-pointer"
                        onClick={() => toggleVideo(video.id)}
                      >
                        <div className="flex items-start space-x-4">
                          <div className="w-12 h-12 rounded-xl bg-teal-500/20 flex items-center justify-center text-teal-400">
                            <FiVideo className="text-xl" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-white mb-1">{video.title}</h3>
                            {video.description && (
                              <p className="text-sm text-gray-400 line-clamp-2">{video.description}</p>
                            )}
                          </div>
                        </div>
                        <button
                          className="text-gray-400 hover:text-white transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleVideo(video.id);
                          }}
                        >
                          {activeVideoId === video.id ? (
                            <FiChevronUp className="text-xl" />
                          ) : (
                            <FiChevronDown className="text-xl" />
                          )}
                        </button>
                      </div>
                    </div>

                    {activeVideoId === video.id && (
                      <div className="px-6 pb-6">
                        <div className="relative w-full pt-[56.25%] bg-black rounded-lg overflow-hidden">
                          <iframe
                            src={getEmbedUrl(video.url)}
                            className="absolute top-0 left-0 w-full h-full"
                            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                            allowFullScreen
                            frameBorder="0"
                            style={{ border: 'none' }}
                            title={video.title}
                            referrerPolicy="origin"
                            sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-downloads allow-presentation"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {filteredVideos.length === 0 && (
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-8 text-center">
            <FiVideo className="mx-auto text-4xl text-gray-400 mb-4" />
            <p className="text-gray-400 text-lg">No videos found</p>
            {searchQuery && (
              <p className="text-gray-500 mt-2">Try adjusting your search query</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Videos;